import { useQueries } from "@tanstack/react-query";
import { type LendingApiClient, useApi } from "@lending/lib/api";
import { STANDARD_QUERY_CACHE_CONFIG } from "@lending/lib/queryConfig";
import { getLoanPortalStatus } from "./portalApplicationStatus";
import type { LoanPortalApplication } from "./types";
import type {
  CreditCardApplication,
  PersonalLoanApplication,
  VehicleLoanApplication,
} from "@lending/types";

function getPersonalLoanApplicationsQueryOptions($api: LendingApiClient) {
  return $api.queryOptions(
    "get",
    "/v2/lending/personal_loan_applications/",
    {},
    STANDARD_QUERY_CACHE_CONFIG
  );
}

function getCreditCardApplicationsQueryOptions($api: LendingApiClient) {
  return $api.queryOptions(
    "get",
    "/v2/lending/credit_card_applications/",
    {},
    STANDARD_QUERY_CACHE_CONFIG
  );
}

function getVehicleLoanApplicationsQueryOptions($api: LendingApiClient) {
  return $api.queryOptions(
    "get",
    "/v2/lending/vehicle_loan_applications/",
    {},
    STANDARD_QUERY_CACHE_CONFIG
  );
}

function mapPersonalLoanApplication(application: PersonalLoanApplication): LoanPortalApplication {
  return {
    firstName: application.primary_applicant?.first_name ?? null,
    lastName: application.primary_applicant?.last_name ?? null,
    uuid: application.id,
    displayId: application.short_id ?? "",
    productType: "personal-loan",
    state: application.state,
    loanPortalStatus: getLoanPortalStatus(application.state),
    amountCents: application.requested_amount_cents ?? null,
    createdAt: application.created_at,
    updatedAt: application.updated_at,
  };
}

function mapCreditCardApplication(application: CreditCardApplication): LoanPortalApplication {
  return {
    firstName: application.primary_applicant?.first_name ?? null,
    lastName: application.primary_applicant?.last_name ?? null,
    uuid: application.id,
    displayId: application.short_id ?? "",
    productType: "credit-card",
    state: application.state,
    loanPortalStatus: getLoanPortalStatus(application.state),
    // The approved limit, once an offer is accepted; null before then.
    amountCents: application.credit_limit_cents ?? null,
    createdAt: application.created_at,
    updatedAt: application.updated_at,
  };
}

function mapVehicleLoanApplication(application: VehicleLoanApplication): LoanPortalApplication {
  return {
    firstName: application.primary_applicant?.first_name ?? null,
    lastName: application.primary_applicant?.last_name ?? null,
    uuid: application.id,
    displayId: application.short_id ?? "",
    productType: "auto-loan",
    state: application.state,
    loanPortalStatus: getLoanPortalStatus(application.state),
    amountCents: application.requested_amount_cents ?? null,
    createdAt: application.created_at,
    updatedAt: application.updated_at,
  };
}

function byUpdatedAtDesc(left: LoanPortalApplication, right: LoanPortalApplication): number {
  return right.updatedAt.localeCompare(left.updatedAt);
}

/**
 * Fetches every application accessible to the current principal by querying the
 * per-product list endpoints in parallel, then merges them into a single
 * `LoanPortalApplication[]` sorted by most recently updated.
 */
export function useGetAccessibleApplicationsQuery() {
  const $api = useApi();

  return useQueries({
    queries: [
      getPersonalLoanApplicationsQueryOptions($api),
      getCreditCardApplicationsQueryOptions($api),
      getVehicleLoanApplicationsQueryOptions($api),
    ],
    combine: ([personalLoans, creditCards, vehicleLoans]) => {
      const isLoading = personalLoans.isLoading || creditCards.isLoading || vehicleLoans.isLoading;
      const isError = personalLoans.isError || creditCards.isError || vehicleLoans.isError;

      const data =
        isLoading || isError
          ? undefined
          : [
              ...(personalLoans.data?.results ?? []).map(mapPersonalLoanApplication),
              ...(creditCards.data?.results ?? []).map(mapCreditCardApplication),
              ...(vehicleLoans.data?.results ?? []).map(mapVehicleLoanApplication),
            ].sort(byUpdatedAtDesc);

      return { data, isLoading, isError };
    },
  });
}
